// src/pages/MontiAlburniPage.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from 'react-i18next';
// ─── Hook: scroll/zoom automatico su POI da ancora ─────────────────────────
function useScrollToPOI(allItems, mapRef, markerRefs) {
    useEffect(() => {
        let retryTimeout = null;
        let retryCount = 0;
        const MAX_RETRIES = 12; // ~6 secondi (500ms x 12)
        function tryScrollToPOI() {
            if (!allItems.length) return;
            const hash = window.location.hash;
            if (hash && hash.startsWith('#poi-')) {
                const poiId = decodeURIComponent(hash.replace('#poi-', ''));
                const item = allItems.find(i => String(i.id) === String(poiId));
                const alt = !item ? allItems.find(i => String(i.id) === 'node:' + String(poiId)) : null;
                const target = item || alt;
                const targetId = target?.id;
                if (target && mapRef.current && markerRefs.current[targetId]) {
                    setTimeout(() => {
                        mapRef.current.flyTo([target.location.lat, target.location.lng], 14, { duration: 0.8 });
                        setTimeout(() => markerRefs.current[targetId]?.openPopup(), 400);
                    }, 400);
                    return true;
                } else if (target && retryCount < MAX_RETRIES) {
                    retryCount++;
                    retryTimeout = setTimeout(tryScrollToPOI, 500);
                }
            }
            return false;
        }
        function handleHashScroll() {
            retryCount = 0;
            if (retryTimeout) clearTimeout(retryTimeout);
            tryScrollToPOI();
        }
        // Primo scroll su mount o quando cambiano i dati
        handleHashScroll();
        // Listener per cambi hash
        window.addEventListener('hashchange', handleHashScroll);
        return () => {
            window.removeEventListener('hashchange', handleHashScroll);
            if (retryTimeout) clearTimeout(retryTimeout);
        };
    }, [allItems, mapRef, markerRefs]);
}
import NearbyPlacesModal from "../components/NearbyPlacesModal";
import ContactLinks from "../components/ContactLinks";
import ActivityPopup from "../components/ActivityPopup";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../styles/MontiAlburni.css";

const API_BASE = process.env.REACT_APP_API_URL_NODE || "";

// ─── Hook: rileva mobile (≤768px) ────────────────────────────────────────────

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 768px)");
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);
    return isMobile;
}

// ─── Costanti ────────────────────────────────────────────────────────────────

// Bounds dell'area Monti Alburni — limitano il caricamento delle tile alla sola area
const ALBURNI_BOUNDS = L.latLngBounds(
    [40.25, 14.95],   // SW
    [40.70, 15.55]    // NE
);
const ALBURNI_CENTER = [40.47, 15.25];
const ALBURNI_ZOOM = 11;

const CATEGORIES = (t) => ({
    agriturismo: { label: t("category_agriturismo", "Agriturismo"), emoji: "🌾", color: "#7cb342" },
    lodging: { label: t("category_lodging", "Ospitalità"), emoji: "🏨", color: "#1e88e5" },
    rifugio: { label: t("category_rifugio", "Rifugi & Casoni"), emoji: "🏕️", color: "#6d4c41" },
    camp_site: { label: t("category_camp_site", "Campeggi"), emoji: "⛺", color: "#43a047" },
    food: { label: t("category_food", "Ristorazione"), emoji: "🍽️", color: "#e53935" },
    local_products: { label: t("category_local_products", "Prodotti Tipici"), emoji: "🧀", color: "#fb8c00" },
    sport: { label: t("category_sport", "Sport & Avventura"), emoji: "🏄", color: "#0288d1" },
    outdoor_guide: { label: t("category_outdoor_guide", "Guide & Escursioni"), emoji: "🧭", color: "#6d4c41" },
    culture: { label: t("category_culture", "Arte & Cultura"), emoji: "🏛️", color: "#8e24aa" },
    info_point: { label: t("category_info_point", "Info & Pro Loco"), emoji: "ℹ️", color: "#00897b" },
    osservatorio: { label: t("category_osservatorio", "Osservatorio"), emoji: "🔭", color: "#3949ab" },
    oasi: { label: t("category_oasi", "Oasi Naturalistiche"), emoji: "🦅", color: "#2e7d32" },
    natura: { label: t("category_natura", "Natura & Geologia"), emoji: "🪨", color: "#795548" },
});

// ─── Marker colorati per categoria ───────────────────────────────────────────

function makeIcon(category, t) {
    const { emoji, color } = CATEGORIES(t)[category] ?? { emoji: "📍", color: "#555" };
    const html = `
        <div class="activity-marker-inner" style="
            background: white;
            border: 2px solid ${color};
            border-radius: 50%;
            width: 32px; height: 32px;
            display: flex; align-items: center; justify-content: center;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            cursor: pointer;
        ">
            <span style="font-size: 18px; line-height: 1;">${emoji}</span>
        </div>`;
    return L.divIcon({
        className: "leaflet-div-icon activity-marker",
        html,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -20],
    });
}

// ─── FitBounds ───────────────────────────────────────────────────────────────

function FitBounds({ items }) {
    const map = useMap();
    useEffect(() => {
        if (!items.length) return;
        const bounds = L.latLngBounds(items.map(i => [i.location.lat, i.location.lng]));
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }, [items, map]);
    return null;
}

// ─── LocateControl: vola sulla posizione utente ───────────────────────────────

function LocateControl({ onLocate }) {
    const map = useMap();
    const [status, setStatus] = useState("idle"); // idle | loading | active | error
    const markerRef = useRef(null);
    const circleRef = useRef(null);

    const handleLocate = () => {
        if (!navigator.geolocation) {
            setStatus("error");
            return;
        }
        setStatus("loading");
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude: lat, longitude: lng, accuracy } = pos.coords;

                // Rimuovi marker/cerchio precedenti
                if (markerRef.current) { map.removeLayer(markerRef.current); }
                if (circleRef.current) { map.removeLayer(circleRef.current); }

                // Cerchio di accuratezza
                circleRef.current = L.circle([lat, lng], {
                    radius: accuracy,
                    color: "#1a73e8", fillColor: "#1a73e8", fillOpacity: 0.1, weight: 1,
                }).addTo(map);

                // Marker posizione
                const icon = L.divIcon({
                    className: "",
                    html: `<div style="
                        width:16px; height:16px; border-radius:50%;
                        background:#1a73e8; border:3px solid white;
                        box-shadow:0 0 0 2px #1a73e8;
                    "></div>`,
                    iconSize: [16, 16],
                    iconAnchor: [8, 8],
                });
                markerRef.current = L.marker([lat, lng], { icon }).addTo(map);

                map.flyTo([lat, lng], 13, { duration: 1 });
                setStatus("active");
                if (onLocate) onLocate(lat, lng);
            },
            () => setStatus("error"),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const colors = {
        idle: { bg: "white", icon: "🎯", title: "Centrami sulla mia posizione" },
        loading: { bg: "white", icon: "⏳", title: "Ricerca posizione..." },
        active: { bg: "#e8f0fe", icon: "🎯", title: "Posizione trovata" },
        error: { bg: "#fdecea", icon: "⚠️", title: "Posizione non disponibile" },
    };
    const c = colors[status];

    return (
        <div style={{
            position: "absolute", bottom: 24, right: 12, zIndex: 1000,
        }}>
            <button
                onClick={handleLocate}
                disabled={status === "loading"}
                title={c.title}
                style={{
                    width: 40, height: 40,
                    borderRadius: 4,
                    background: c.bg,
                    border: "2px solid rgba(0,0,0,0.2)",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                    cursor: status === "loading" ? "wait" : "pointer",
                    fontSize: 18,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "background 0.2s",
                }}
            >
                {c.icon}
            </button>
        </div>
    );
}

// ─── ActivityCard ─────────────────────────────────────────────────────────────

function ActivityCard({ item, onFocus }) {
    const { t } = useTranslation();
    const cat = CATEGORIES(t)[item.category];
    return (
        <div
            onClick={() => onFocus(item)}
            style={{
                padding: "12px 14px", marginBottom: 10, borderRadius: 10,
                background: "white", cursor: "pointer",
                border: "2px solid transparent",
                boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                transition: "border-color 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = cat?.color ?? "#ccc"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "transparent"}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <strong style={{ fontSize: 15, flex: 1 }}>{item.name ?? "—"}</strong>
                <span style={{
                    fontSize: 11, padding: "2px 8px", borderRadius: 20, marginLeft: 8,
                    background: cat?.color ?? "#eee", color: "white", whiteSpace: "nowrap",
                }}>
                    {cat?.emoji} {cat?.label}
                </span>
            </div>
            {item.municipality && (
                <div style={{ fontSize: 13, color: "#666", marginTop: 3 }}>
                    📍 {item.municipality}
                </div>
            )}
            {item.notOnOSM && (
                <div style={{ fontSize: 11, color: "#333", marginTop: 3 }}>
                    ⚠️ Non presente su OpenStreetMap
                </div>
            )}
            <ContactLinks
                contacts={item.contacts}
                entityId={item.id}
                entityType="activity"
                placement="list_card"
            />
        </div>
    );
}

// ─── Tab bar (solo mobile) ────────────────────────────────────────────────────

function MobileTabBar({ activeTab, onTabChange, listCount }) {
    return (
        <div style={{
            display: "flex", flexShrink: 0,
            borderBottom: "2px solid #e0e0e0",
            background: "white",
        }}>
            {[
                { id: "map", label: "🗺️ Mappa" },
                { id: "list", label: `📋 Lista (${listCount})` },
            ].map(tab => (
                <button
                    key={tab.id}
                    onClick={() => onTabChange(tab.id)}
                    style={{
                        flex: 1, padding: "10px 0", fontSize: 14, fontWeight: 600,
                        border: "none", cursor: "pointer",
                        background: activeTab === tab.id ? "#f0f7f0" : "white",
                        color: activeTab === tab.id ? "#2d5a27" : "#666",
                        borderBottom: activeTab === tab.id
                            ? "3px solid #2d5a27"
                            : "3px solid transparent",
                        transition: "all 0.15s",
                    }}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    );
}

// ─── Header mobile compatto ───────────────────────────────────────────────────
// Su mobile mostriamo solo una barra sottile con titolo + contatore,
// invece del grande hero che occupa ~120px.

function MobileHeader({ count }) {
    return (
        <div style={{
            flexShrink: 0,
            background: "linear-gradient(90deg, #2d5a27, #5a8a2a)",
            color: "white",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
        }}>
            <span style={{ fontWeight: 800, fontSize: 17 }}>🏔️ Monti Alburni</span>
            {count > 0 && (
                <span style={{ fontSize: 12, opacity: 0.85, background: "rgba(255,255,255,0.2)", borderRadius: 12, padding: "2px 10px" }}>
                    {count} attività
                </span>
            )}
        </div>
    );
}

// ─── Filtri + search compatti per mobile ─────────────────────────────────────
// I chip scorrono su una singola riga orizzontale (no wrap).

function MobileControls({ allItems, counts, activeCategory, setActiveCategory, search, setSearch }) {
    const { t } = useTranslation();
    return (
        <>
            {/* Chip: scroll orizzontale, riga singola */}
            <div style={{
                flexShrink: 0,
                display: "flex",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                gap: 6,
                padding: "8px 12px",
                background: "#f5f5f0",
                scrollbarWidth: "none",          // Firefox
            }}>
                <style>{`.ma-chips::-webkit-scrollbar { display: none; }`}</style>
                {/* Il div interno serve per applicare la classe scrollbar-hide */}
                <button
                    onClick={() => setActiveCategory(null)}
                    style={{ ...chipStyle(activeCategory === null, "#555"), flexShrink: 0 }}
                >
                    🗺️ Tutte ({allItems.length})
                </button>
                {Object.entries(CATEGORIES(t)).map(([key, cat]) => counts[key] ? (
                    <button
                        key={key}
                        onClick={() => setActiveCategory(key === activeCategory ? null : key)}
                        style={{ ...chipStyle(activeCategory === key, cat.color), flexShrink: 0 }}
                    >
                        {cat.emoji} {cat.label} ({counts[key]})
                    </button>
                ) : null)}
            </div>

            {/* Search */}
            <div style={{ flexShrink: 0, padding: "6px 12px 8px", background: "#f5f5f0" }}>
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Cerca per nome o comune..."
                    style={{
                        width: "100%", padding: "8px 12px", borderRadius: 8, fontSize: 14,
                        border: "1px solid #ccc", boxSizing: "border-box", outline: "none",
                    }}
                />
            </div>
        </>
    );
}


// ─── Pagina principale ────────────────────────────────────────────────────────
export default function MontiAlburniPage() {
    const [allItems, setAllItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [activeCategory, setActiveCategory] = useState(null);
    const [search, setSearch] = useState("");
    const [mobileTab, setMobileTab] = useState("map");
    const [mapMounted, setMapMounted] = useState(!window.matchMedia("(max-width: 768px)").matches); // lazy su mobile
    const mapRef = useRef(null);
    const markerRefs = useRef({});
    const isMobile = useIsMobile();
    // Scroll/zoom automatico su POI da ancora
    useScrollToPOI(allItems, mapRef, markerRefs);

    // Stato e logica per la modale "Nei dintorni"
    const [nearbyModal, setNearbyModal] = useState({ open: false, lat: null, lng: null, name: "" });
    function handleShowNearby(lat, lng, name) {
        setNearbyModal({ open: true, lat, lng, name });
    }
    function handleCloseNearby() {
        setNearbyModal({ open: false, lat: null, lng: null, name: "" });
    }

    useEffect(() => {
        fetch(`${API_BASE}/api/areas/monti-alburni/activities`)
            .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
            .then(data => setAllItems(data.items ?? []))
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    // ...tutto il resto del componente rimane invariato...

    const filtered = useMemo(() => {
        let items = allItems;
        if (activeCategory) items = items.filter(i => i.category === activeCategory);
        if (search.trim()) items = items.filter(i =>
            i.name?.toLowerCase().includes(search.toLowerCase()) ||
            i.address?.city?.toLowerCase().includes(search.toLowerCase()) ||
            i.municipality?.toLowerCase().includes(search.toLowerCase())
        );
        return items;
    }, [allItems, activeCategory, search]);

    const counts = useMemo(() =>
        allItems.reduce((acc, i) => { acc[i.category] = (acc[i.category] ?? 0) + 1; return acc; }, {})
        , [allItems]);

    const handleFocus = (item) => {
        if (isMobile) {
            setMapMounted(true);
            setMobileTab("map");
        }
        const doFly = () => {
            mapRef.current?.flyTo([item.location.lat, item.location.lng], 14, { duration: 0.8 });
            setTimeout(() => markerRefs.current[item.id]?.openPopup(), 400);
        };
        setTimeout(doFly, isMobile ? 150 : 0);
    };

    // ── Pannello mappa ────────────────────────────────────────────────────────

    const mapPanel = (
        <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
            <MapContainer
                center={ALBURNI_CENTER}
                zoom={ALBURNI_ZOOM}
                minZoom={9}
                maxZoom={17}
                maxBounds={ALBURNI_BOUNDS}
                maxBoundsViscosity={0.8}
                style={{ height: "100%", width: "100%" }}
                ref={mapRef}
            >
                <TileLayer
                    url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                    attribution="© OpenTopoMap contributors"
                    maxZoom={17}
                />
                <FitBounds items={filtered} />
                <LocateControl />
                {filtered.map(item => (
                    <Marker
                        key={item.id}
                        position={[item.location.lat, item.location.lng]}
                        icon={makeIcon(item.category, t)}
                        ref={el => { if (el) markerRefs.current[item.id] = el; }}
                    >
                        <Popup minWidth={240} maxWidth={300}>
                            <ActivityPopup item={item} placement="map_popup" onShowNearby={() => handleShowNearby(item.location.lat, item.location.lng, item.name)} />
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>

            {/* Modale "Nei dintorni" */}
            <NearbyPlacesModal
                open={nearbyModal.open}
                lat={nearbyModal.lat}
                lng={nearbyModal.lng}
                name={nearbyModal.name}
                onClose={handleCloseNearby}
            />
        </div>
    );

    // ── Pannello lista ────────────────────────────────────────────────────────

    const listPanel = (
        <div style={{
            flex: "1 1 40%",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            padding: "12px 12px 80px",
            background: "#f9f9f7",
            maxWidth: isMobile ? "none" : 420,
            minHeight: 0,
        }}>
            {filtered.length === 0
                ? <p style={{ textAlign: "center", color: "#999", paddingTop: 40 }}>Nessuna attività trovata</p>
                : filtered.map(item => (
                    <ActivityCard key={item.id} item={item} onFocus={handleFocus} />
                ))
            }
        </div>
    );

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", fontFamily: "sans-serif", background: "#f5f5f0" }}>

            {isMobile ? (
                // ── MOBILE: header compatto + chip orizzontali ────────────────
                <>
                    <MobileHeader count={allItems.length} />
                    <MobileControls
                        allItems={allItems}
                        counts={counts}
                        activeCategory={activeCategory}
                        setActiveCategory={setActiveCategory}
                        search={search}
                        setSearch={setSearch}
                    />
                </>
            ) : (
                // ── DESKTOP: hero grande + chip wrap + search centrata ─────────
                <>
                    <div style={{
                        flexShrink: 0,
                        background: "linear-gradient(135deg, #2d5a27 0%, #5a8a2a 50%, #3d7a3d 100%)",
                        color: "white", padding: "48px 24px 40px", textAlign: "center",
                    }}>
                        <h1 style={{ margin: 0, fontSize: "clamp(28px, 6vw, 48px)", fontWeight: 800, letterSpacing: -1 }}>
                            🏔️ {t('alburni_mountains')}
                        </h1>
                        <p style={{ margin: "12px auto 0", maxWidth: 560, fontSize: "clamp(14px, 2.5vw, 18px)", opacity: 0.9, lineHeight: 1.5 }}>
                            {t('alburni_subtitle')}
                        </p>
                        {!loading && (
                            <div style={{ marginTop: 16, fontSize: 14, opacity: 0.8 }}>
                                {allItems.length} {t('available_activities')}
                            </div>
                        )}
                    </div>
                    <div style={{
                        flexShrink: 0,
                        display: "flex", flexWrap: "wrap", gap: 8,
                        padding: "16px 16px 0", justifyContent: "center", background: "#f5f5f0",
                    }}>
                        <button onClick={() => setActiveCategory(null)} style={chipStyle(activeCategory === null, "#555")}>
                            🗺️ Tutte ({allItems.length})
                        </button>
                        {Object.entries(CATEGORIES(t)).map(([key, cat]) => counts[key] ? (
                            <button key={key} onClick={() => setActiveCategory(key === activeCategory ? null : key)}
                                style={chipStyle(activeCategory === key, cat.color)}>
                                {cat.emoji} {cat.label} ({counts[key]})
                            </button>
                        ) : null)}
                    </div>
                    <div style={{ flexShrink: 0, padding: "12px 16px", maxWidth: 400, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Cerca per nome o comune..."
                            style={{
                                width: "100%", padding: "10px 14px", borderRadius: 8, fontSize: 15,
                                border: "1px solid #ccc", boxSizing: "border-box", outline: "none",
                            }}
                        />
                    </div>
                </>
            )}

            {loading && <p style={{ textAlign: "center", padding: 40 }}>Caricamento...</p>}
            {error && <p style={{ textAlign: "center", padding: 40, color: "red" }}>Errore: {error}</p>}

            {!loading && !error && (
                isMobile ? (
                    <>
                        <MobileTabBar
                            activeTab={mobileTab}
                            onTabChange={(tab) => {
                                if (tab === "map") setMapMounted(true);
                                setMobileTab(tab);
                            }}
                            listCount={filtered.length}
                        />
                        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                            <div style={{ flex: 1, display: mobileTab === "map" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
                                {mapMounted && mapPanel}
                            </div>
                            <div style={{ flex: 1, display: mobileTab === "list" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
                                {listPanel}
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{ flex: 1, overflow: "hidden", display: "flex", minHeight: 0 }}>
                        {mapPanel}
                        {listPanel}
                    </div>
                )
            )}

            <a href="https://aihiking.eu" target="_blank" rel="noopener noreferrer"
                style={{
                    position: "fixed", bottom: 16, right: 16, zIndex: 1000,
                    background: "white", border: "1px solid #ddd", borderRadius: 20,
                    padding: "6px 14px", fontSize: 12, color: "#555",
                    textDecoration: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    display: "flex", alignItems: "center", gap: 6,
                }}>
                🤖 Powered by <strong>AI Hiking</strong>
            </a>

        </div>
    );
}
function chipStyle(active, color) {
    return {
        padding: "7px 14px", borderRadius: 20, fontSize: 13, cursor: "pointer",
        border: `2px solid ${color}`,
        background: active ? color : "white",
        color: active ? "white" : color,
        fontWeight: active ? 700 : 400,
        transition: "all 0.15s",
        whiteSpace: "nowrap",
    };
}
